import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { UserPlus, User, Lock, ArrowRight, Activity, ShieldCheck } from 'lucide-react';
import auth from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const result = auth.register(formData.username, formData.password, 'user');
      
      if (result.success) {
        toast.success('Registration successful! Please login.');
        navigate('/login');
      } else {
        toast.error(result.error || 'Registration failed');
      }
    } catch (error) {
      toast.error('An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white relative overflow-hidden">
      {/* Grid Pattern Background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.5) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(15, 23, 42, 0.5) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }} />

      {/* Background Image - Grayscale */}
      <div 
        className="absolute inset-0 opacity-10 grayscale"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758670496782-cb11b7b56675?q=80&w=2000&auto=format&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      {/* Abstract Elements */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-primary opacity-5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-48 h-48 bg-accent opacity-10 rounded-none" />
      
      {/* Main Card */}
      <div className="w-full max-w-md relative z-10">
        {/* System Status Badge */}
        

        {/* Main Card with Swiss Brutalist Style */}
        <div className="bg-white border-8 border-slate-900 shadow-[15px_15px_0px_0px_rgba(0,47,167,1)] relative">
          {/* Header */}
          <div className="p-8 border-b-4 border-slate-900 bg-primary text-white">
            <div className="flex items-center justify-center mb-4">
              <div className="w-20 h-20 bg-white flex items-center justify-center border-4 border-white">
                <UserPlus className="text-primary" size={40} />
              </div>
            </div>
            <h1 className="text-4xl font-black font-heading tracking-tighter text-center uppercase mb-2">
              Create Account
            </h1>
            <p className="text-center text-blue-100 font-medium uppercase text-sm tracking-wide">
              System Registration Portal
            </p>
          </div>

          {/* Form Content */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username Field */}
              <div className="space-y-3">
                <Label htmlFor="username" className="text-sm font-black uppercase tracking-widest text-slate-900">
                  Username
                </Label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-12 bg-slate-900 flex items-center justify-center border-r-4 border-slate-900">
                    <User className="text-white" size={18} />
                  </div>
                  <Input
                    id="username"
                    data-testid="register-username"
                    type="text"
                    placeholder="CHOOSE USERNAME"
                    className="pl-16 h-14 border-4 border-slate-900 rounded-none font-mono text-sm uppercase placeholder:text-slate-400 focus:border-primary focus:ring-0 transition-colors"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-3">
                <Label htmlFor="password" className="text-sm font-black uppercase tracking-widest text-slate-900">
                  Password
                </Label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-12 bg-slate-900 flex items-center justify-center border-r-4 border-slate-900">
                    <Lock className="text-white" size={18} />
                  </div>
                  <Input
                    id="password"
                    data-testid="register-password"
                    type="password"
                    placeholder="MIN 6 CHARACTERS"
                    className="pl-16 h-14 border-4 border-slate-900 rounded-none font-mono text-sm uppercase placeholder:text-slate-400 focus:border-primary focus:ring-0 transition-colors"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-3">
                <Label htmlFor="confirmPassword" className="text-sm font-black uppercase tracking-widest text-slate-900">
                  Confirm Password
                </Label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-12 bg-slate-900 flex items-center justify-center border-r-4 border-slate-900">
                    <Lock className="text-white" size={18} />
                  </div>
                  <Input
                    id="confirmPassword"
                    data-testid="register-confirm-password"
                    type="password"
                    placeholder="CONFIRM PASSWORD"
                    className="pl-16 h-14 border-4 border-slate-900 rounded-none font-mono text-sm uppercase placeholder:text-slate-400 focus:border-primary focus:ring-0 transition-colors"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Security Notice */}
              <div className="bg-slate-50 border-4 border-slate-900 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="text-primary flex-shrink-0 mt-1" size={20} />
                  <div className="space-y-1">
                    <p className="text-xs font-black uppercase tracking-wide text-slate-900">
                      Security Requirements
                    </p>
                    <ul className="text-xs text-slate-600 space-y-1 font-medium">
                      <li>• Minimum 6 characters required</li>
                      <li>• Passwords must match exactly</li>
                      <li>• Account registered as standard user</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                data-testid="register-submit-button"
                className="w-full h-16 font-black uppercase tracking-wide text-lg rounded-none border-4 border-slate-900 shadow-[5px_5px_0px_0px_rgba(15,23,42,1)] hover:shadow-[7px_7px_0px_0px_rgba(15,23,42,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all group relative overflow-hidden"
                disabled={loading}
              >
                <span className="relative z-10 flex items-center justify-center">
                  {loading ? 'CREATING ACCOUNT...' : 'REGISTER SYSTEM'}
                  {!loading && <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" size={20} />}
                </span>
                {!loading && (
                  <div className="absolute inset-0 bg-accent transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                )}
              </Button>
            </form>

            {/* Footer Links */}
            <div className="mt-8 pt-6 border-t-4 border-slate-900">
              <div className="text-center">
                <p className="text-sm font-bold uppercase tracking-wide text-slate-600">
                  Already Registered?{' '}
                  <Link 
                    to="/login" 
                    className="text-primary font-black hover:underline inline-flex items-center gap-1 group"
                  >
                    Sign In
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Bar */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="bg-white border-4 border-slate-900 p-3 text-center hover:bg-slate-50 transition-colors">
            <div className="text-2xl font-black text-primary mb-1">FREE</div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-600">14-Day Trial</div>
          </div>
          <div className="bg-white border-4 border-slate-900 p-3 text-center hover:bg-slate-50 transition-colors">
            <div className="text-2xl font-black text-accent mb-1">ZERO</div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-600">Setup Fees</div>
          </div>
          <div className="bg-white border-4 border-slate-900 p-3 text-center hover:bg-slate-50 transition-colors">
            <div className="text-2xl font-black text-green-600 mb-1">24/7</div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-600">Support</div>
          </div>
        </div>

        {/* Back to Home Link */}
        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 transition-colors group"
          >
            <ArrowRight size={16} className="rotate-180 group-hover:-translate-x-1 transition-transform" />
            Return to Main System
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}